'use client'

import Link from 'next/link'
import { cn } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'
import type { ChatAttachment, PolicyAttachmentData } from '@/lib/ai/types'

/** "link" attachment — a button to an app route. */
export function LinkCard({ attachment, onNavigate }: { attachment: ChatAttachment; onNavigate?: () => void }) {
  if (!attachment.url) return null
  const external = /^https?:\/\//i.test(attachment.url)
  const className = cn(
    'inline-flex items-center gap-2 rounded-xl border border-brand-cornflower/30 bg-white px-3 py-2',
    'text-sm font-medium text-brand-navy shadow-soft transition-colors hover:bg-brand-cornflower/10'
  )
  const body = (
    <>
      {external ? <Icons.externalLink className="h-4 w-4 text-brand-cornflower" strokeWidth={1.5} /> : <Icons.arrowRight className="h-4 w-4 text-brand-cornflower" strokeWidth={1.5} />}
      <span>{attachment.title}</span>
      {attachment.description && <span className="text-xs font-normal text-muted-foreground">· {attachment.description}</span>}
    </>
  )
  return external ? (
    <a href={attachment.url} target="_blank" rel="noopener noreferrer" className={className}>{body}</a>
  ) : (
    <Link href={attachment.url} onClick={onNavigate} className={className}>{body}</Link>
  )
}

/** "policy" attachment — chip deep-linking to /ai/policies?highlight=<rule_code>. */
export function PolicyChip({ attachment, onNavigate }: { attachment: ChatAttachment; onNavigate?: () => void }) {
  const data = (attachment.data ?? {}) as PolicyAttachmentData
  const code = data.rule_code || data.id
  const href = attachment.url || (code ? `/ai/policies?highlight=${encodeURIComponent(code)}` : '/ai/policies')
  return (
    <Link
      href={href}
      onClick={onNavigate}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border border-brand-purple/30 bg-brand-purple/5 px-2.5 py-1',
        'text-xs font-medium text-brand-purple transition-colors hover:bg-brand-purple/10'
      )}
      title={attachment.description ?? undefined}
    >
      <Icons.brain className="h-3.5 w-3.5" strokeWidth={1.5} />
      {data.rule_code && <span className="font-mono">{data.rule_code}</span>}
      <span>{data.name || attachment.title}</span>
    </Link>
  )
}
